/**
 * IconButton — square button for icon-only actions (theme T5a §1.8).
 *
 * Requires `aria-label`.  For row-level actions inside tables, prefer
 * `variant="tertiary" size="sm"`.
 */
import type { ButtonHTMLAttributes, ComponentType } from 'react';
import { forwardRef } from 'react';

import { cn } from './cn';
import { Spinner } from './Spinner';

type Size = 'sm' | 'md' | 'lg';
type Variant = 'primary' | 'secondary' | 'tertiary' | 'destructive';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  'aria-label': string;
  icon: ComponentType<{ size?: number; strokeWidth?: number; 'aria-hidden'?: boolean }>;
  size?: Size;
  variant?: Variant;
  isLoading?: boolean;
  /** Circular instead of rounded-lg.  Use for close-on-modal + avatar-adjacent. */
  circular?: boolean;
}

const SIZE_CLASSES: Record<Size, { square: string; icon: number }> = {
  sm: { square: 'h-7 w-7', icon: 14 },
  md: { square: 'h-9 w-9', icon: 16 },
  lg: { square: 'h-11 w-11', icon: 20 },
};

const VARIANT_CLASSES: Record<Variant, string> = {
  primary: 'bg-action text-action-fg hover:bg-action-hover active:bg-action-active',
  secondary:
    'bg-transparent text-slate-700 border border-slate-300 hover:bg-slate-50 active:bg-slate-100',
  tertiary: 'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-800',
  destructive: 'bg-error-600 text-white hover:bg-error-700',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    icon: Icon,
    size = 'md',
    variant = 'tertiary',
    isLoading = false,
    circular = false,
    className,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const { square, icon: iconSize } = SIZE_CLASSES[size];
  return (
    <button
      ref={ref}
      aria-busy={isLoading || undefined}
      className={cn(
        'duration-DEFAULT inline-flex items-center justify-center transition-colors ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-disabled',
        circular ? 'rounded-full' : 'rounded-lg',
        VARIANT_CLASSES[variant],
        square,
        className,
      )}
      disabled={isLoading || disabled}
      type={type}
      {...rest}
    >
      {isLoading ? (
        <Spinner aria-label="" size={iconSize} />
      ) : (
        <Icon aria-hidden size={iconSize} strokeWidth={1.75} />
      )}
    </button>
  );
});
